import React, { useCallback, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import AutocompleteDropdown from './AutocompleteDropdown';
import FarmacoModal from './FarmacoModal';
import FarmacoService from '../../services/farmacoService';
import './FarmacoAutocomplete.css';

const FarmacoAutocomplete = ({
  selectedFarmaco = null,
  onFarmacoSelect = () => {},
  placeholder = "Seleccionar fármaco",
  searchPlaceholder = "Buscar fármaco...",
  disabled = false,
  className = '',
  maxResults = 15,
  minSearchLength = 2,
  onError = null, // Optional error handler
  ...props
}) => {
  const auth = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch function for farmacos search
  const fetchFarmacos = useCallback(async (searchTerm, token) => {
    if (!searchTerm || searchTerm.length < minSearchLength) {
      return [];
    }

    try {
      const response = await FarmacoService.searchFarmacos(searchTerm, token);
      return response.data || response || [];
    } catch (error) {
      console.error('Error searching farmacos:', error);
      throw error;
    }
  }, [minSearchLength]);

  // Display function for farmaco
  const displayFarmaco = useCallback((farmaco) => {
    if (!farmaco) return '';
    const name = farmaco.commercialName || farmaco.components || 'Fármaco sin nombre';
    const dose = farmaco.dosis ? ` - ${farmaco.dosis}` : '';
    return `${name}${dose}`;
  }, []);

  // Key function for farmaco
  const getFarmacoKey = useCallback((farmaco) => {
    return farmaco?.id || farmaco?.commercialName || farmaco?.components || '';
  }, []);

  // Custom render function for farmaco items
  const renderFarmacoItem = useCallback((farmaco, isSelected, isHighlighted) => {
    return (
      <div className={`autocomplete-item ${isSelected ? 'selected' : ''}`}>
        <div className="farmaco-info">
          <div className="farmaco-name">
            {farmaco.commercialName || farmaco.components || 'Fármaco sin nombre'}
          </div>
          <div className="farmaco-details">
            {farmaco.dosis && (
              <span className="farmaco-dose">
                Dosis: {farmaco.dosis}
              </span>
            )}
            {farmaco.administrationForm && (
              <span className="farmaco-administration">
                {farmaco.administrationForm}
              </span>
            )}
            {farmaco.commercialName && farmaco.components && (
              <span className="farmaco-components">
                {farmaco.components}
              </span>
            )}
          </div>
        </div>
        {isSelected && (
          <i className="fas fa-check selected-icon"></i>
        )}
      </div>
    );
  }, []);

  // Custom render function for selected farmaco preview
  const renderSelectedPreview = useCallback((farmaco) => {
    return (
      <div className="selected-farmaco-preview">
        <span className="farmaco-name">
          {farmaco.commercialName || farmaco.components || 'Fármaco sin nombre'}
        </span>
        {farmaco.dosis && (
          <span className="farmaco-dose">
            {farmaco.dosis}
          </span>
        )}
        {farmaco.administrationForm && (
          <span className="farmaco-name">
            - {farmaco.administrationForm}
          </span>
        )}
      </div>
    );
  }, []);

  // Custom render function for empty state
  const renderEmptyState = useCallback((searchTerm) => {
    return (
      <div className="empty-farmaco-state">
        <div className="empty-message">
          <i className="fas fa-search"></i>
          <span>No se encontraron fármacos</span>
        </div>
      </div>
    );
  }, []);

  // Custom render function for always visible "Add farmaco" option
  const renderAlwaysVisible = useCallback((searchTerm, closeDropdown) => {
    return (
      <div className="add-farmaco-option">
        <button
          type="button"
          className="btn-add-farmaco"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (closeDropdown) closeDropdown();
            setShowCreateModal(true);
          }}
        >
          <i className="fas fa-plus"></i>
          <span>Agregar nuevo fármaco</span>
        </button>
      </div>
    );
  }, []);

  // Handle farmaco creation
  const handleFarmacoCreated = useCallback((newFarmaco) => {
    // Automatically select the newly created farmaco
    onFarmacoSelect(newFarmaco);
    setShowCreateModal(false);
  }, [onFarmacoSelect]);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setShowCreateModal(false);
  }, []);

  return (
    <>
      <AutocompleteDropdown
        fetchFunction={fetchFarmacos}
        displayFunction={displayFarmaco}
        keyFunction={getFarmacoKey}
        selectedItem={selectedFarmaco}
        onItemSelect={onFarmacoSelect}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        disabled={disabled}
        icon="fas fa-pills"
        maxResults={maxResults}
        minSearchLength={minSearchLength}
        debounceDelay={300}
        showClearButton={true}
        emptyMessage="No se encontraron fármacos"
        loadingMessage="Buscando fármacos..."
        renderCustomItem={renderFarmacoItem}
        renderSelectedPreview={renderSelectedPreview}
        renderEmptyState={renderEmptyState}
        renderAlwaysVisible={renderAlwaysVisible}
        token={auth.user?.access_token}
        className={`farmaco-autocomplete ${className}`}
        searchOnFocus={false}
        clearOnSelect={false}
        {...props}
      />
      
      <FarmacoModal
        isOpen={showCreateModal}
        onClose={handleModalClose}
        onFarmacoCreated={handleFarmacoCreated}
      />
    </>
  );
};

export default FarmacoAutocomplete;
